import { useState, useEffect, useCallback } from 'react';
import { View, Text, Modal, ScrollView, StyleSheet, Alert, Pressable, Platform } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { Contact, ContactType, CreateContactRequest, UpdateContactRequest } from '../../shared/types/contact';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { addColorOpacity } from '../../shared/utils/styles';

export interface ContactFormModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: CreateContactRequest | UpdateContactRequest) => Promise<void>;
  contact?: Contact | null;
  contactTypes: ContactType[];
  loading?: boolean;
}

interface FormErrors {
  contactTypeId?: string;
  firstName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  website?: string;
}

export function ContactFormModal({
  visible,
  onClose,
  onSubmit,
  contact,
  contactTypes,
  loading = false,
}: ContactFormModalProps) {
  const styles = useStyles();
  const responsive = useResponsive();
  const { tokens } = useTheme();

  // Form state
  const [contactTypeId, setContactTypeId] = useState<string>('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('USA');
  const [website, setWebsite] = useState('');
  const [notes, setNotes] = useState('');

  // Validation state
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens/closes or contact changes
  useEffect(() => {
    if (visible) {
      if (contact) {
        setContactTypeId(contact.contact_type.id.toString());
        setFirstName(contact.first_name || '');
        setLastName(contact.last_name || '');
        setCompanyName(contact.company_name || '');
        setJobTitle(contact.job_title || '');
        setEmail(contact.email || '');
        setPhone(contact.phone || '');
        setMobile(contact.mobile || '');
        setAddressLine1(contact.address_line1 || '');
        setAddressLine2(contact.address_line2 || '');
        setCity(contact.city || '');
        setState(contact.state || '');
        setZipCode(contact.zip_code || '');
        setCountry(contact.country || 'USA');
        setWebsite(contact.website || '');
        setNotes(contact.notes || '');
      } else {
        resetForm();
      }
      setErrors({});
    }
  }, [contact, visible]);

  const resetForm = useCallback(() => {
    setContactTypeId('');
    setFirstName('');
    setLastName('');
    setCompanyName('');
    setJobTitle('');
    setEmail('');
    setPhone('');
    setMobile('');
    setAddressLine1('');
    setAddressLine2('');
    setCity('');
    setState('');
    setZipCode('');
    setCountry('USA');
    setWebsite('');
    setNotes('');
    setErrors({});
  }, []);

  // Validation functions
  const validateEmail = (email: string): boolean => {
    if (!email) return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUrl = (url: string): boolean => {
    if (!url) return true;
    try {
      const urlWithProtocol = url.startsWith('http://') || url.startsWith('https://') 
        ? url 
        : `https://${url}`;
      new URL(urlWithProtocol);
      return true;
    } catch {
      return false;
    }
  };

  const validatePhone = (phone: string): boolean => {
    if (!phone) return true;
    const digitsOnly = phone.replace(/[\s\-\(\)\.]/g, '');
    return /^\d{7,15}$/.test(digitsOnly);
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!contactTypeId) {
      newErrors.contactTypeId = 'Contact type is required';
    }

    if (!firstName.trim() && !companyName.trim()) {
      newErrors.firstName = 'Either first name or company name is required';
      newErrors.companyName = 'Either first name or company name is required';
    }

    if (email && !validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (phone && !validatePhone(phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (mobile && !validatePhone(mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number';
    }

    if (website && !validateUrl(website)) {
      newErrors.website = 'Please enter a valid website URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please fix the errors in the form before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const data: CreateContactRequest | UpdateContactRequest = {
        contact_type_id: parseInt(contactTypeId, 10),
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        company_name: companyName.trim() || undefined,
        job_title: jobTitle.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        mobile: mobile.trim() || undefined,
        address_line1: addressLine1.trim() || undefined,
        address_line2: addressLine2.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim() || undefined,
        zip_code: zipCode.trim() || undefined,
        country: country.trim() || undefined,
        website: website.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      await onSubmit(data);
      resetForm();
      onClose();
    } catch (error: any) {
      const errorMessage = error?.message || error?.data?.message || 'Failed to save contact. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const selectedType = contactTypes.find((t) => t.id.toString() === contactTypeId);
  const isFormValid = contactTypeId && (firstName.trim() || companyName.trim());
  const isDisabled = loading || isSubmitting || !isFormValid;
  const isDesktop = responsive.isDesktop || responsive.isLargeDesktop;
  const { isMobile, isSmallMobile } = responsive;

  const modalStyles = makeStyles(styles, responsive, isDesktop, tokens, isMobile, isSmallMobile);

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={handleClose}
    >
      <Pressable style={styles.modalOverlay} onPress={handleClose}>
        <Pressable
          style={[styles.modalContent, modalStyles.modalContentCustom]}
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View style={modalStyles.header}>
            <View style={modalStyles.headerContent}>
              <Text style={modalStyles.title}>
                {contact ? 'Edit Contact' : 'Add Contact'}
              </Text>
              <Text style={modalStyles.subtitle}>
                {contact ? 'Update contact information' : 'Create a new professional contact'}
              </Text>
            </View>
            <Pressable 
              onPress={handleClose} 
              style={({ pressed }) => [
                modalStyles.closeButton,
                pressed && modalStyles.closeButtonPressed
              ]}
              disabled={isSubmitting}
            >
              <Text style={modalStyles.closeButtonText}>✕</Text>
            </Pressable>
          </View>

          {/* Scrollable Content */}
          <ScrollView 
            style={modalStyles.scrollView}
            contentContainerStyle={modalStyles.scrollContent}
            showsVerticalScrollIndicator={true}
            keyboardShouldPersistTaps="handled"
            bounces={false}
          >
            {/* Contact Type Section */}
            <View style={modalStyles.section}>
              <Text style={modalStyles.sectionTitle}>
                Contact Type <Text style={modalStyles.required}>*</Text>
              </Text>
              <Text style={modalStyles.sectionHint}>
                Select the type that best describes this contact
              </Text>
              {isMobile ? (
                // Wrapping grid for mobile
                <View style={modalStyles.typeSelectorContent}>
                  {contactTypes.map((type) => {
                    const isSelected = contactTypeId === type.id.toString();
                    return (
                      <Pressable
                        key={type.id}
                        onPress={() => {
                          setContactTypeId(type.id.toString());
                          if (errors.contactTypeId) {
                            setErrors(prev => ({ ...prev, contactTypeId: undefined }));
                          }
                        }}
                        style={({ pressed }) => [
                          modalStyles.typeChip,
                          isSelected && modalStyles.typeChipSelected,
                          pressed && modalStyles.typeChipPressed,
                        ]}
                        disabled={isSubmitting}
                      >
                        <Text
                          style={[
                            modalStyles.typeChipText,
                            isSelected && modalStyles.typeChipTextSelected,
                          ]}
                        >
                          {type.name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </Text>
                      </Pressable>
                    );
                  })}
                </View>
              ) : (
                // Horizontal scroll for desktop
                <ScrollView 
                  horizontal 
                  showsHorizontalScrollIndicator={false}
                  style={modalStyles.typeSelector}
                  contentContainerStyle={modalStyles.typeSelectorContent}
                >
                  {contactTypes.map((type) => {
                    const isSelected = contactTypeId === type.id.toString();
                    return (
                      <Pressable
                        key={type.id}
                        onPress={() => {
                          setContactTypeId(type.id.toString());
                          if (errors.contactTypeId) {
                            setErrors(prev => ({ ...prev, contactTypeId: undefined }));
                          }
                        }}
                        style={({ pressed }) => [
                          modalStyles.typeChip,
                          isSelected && modalStyles.typeChipSelected,
                          pressed && modalStyles.typeChipPressed,
                        ]}
                        disabled={isSubmitting}
                      >
                        <Text
                          style={[
                            modalStyles.typeChipText,
                            isSelected && modalStyles.typeChipTextSelected,
                          ]}
                        >
                          {type.name.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                        </Text>
                      </Pressable>
                    );
                  })}
                </ScrollView>
              )}
              {errors.contactTypeId && (
                <Text style={modalStyles.errorText}>{errors.contactTypeId}</Text>
              )}
              {selectedType && !errors.contactTypeId && (
                <Text style={modalStyles.selectedTypeHint}>
                  {selectedType.description || `Selected: ${selectedType.name}`}
                </Text>
              )}
            </View>

            {/* Form Fields - Single Column Always */}
            <View style={modalStyles.formContainer}>
              {/* Basic Information */}
              <View style={modalStyles.section}>
                <Text style={modalStyles.sectionTitle}>Basic Information</Text>
                <View style={modalStyles.row}>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="First Name"
                      value={firstName}
                      onChangeText={(text) => {
                        setFirstName(text);
                        if (errors.firstName) {
                          setErrors(prev => ({ ...prev, firstName: undefined }));
                        }
                      }}
                      placeholder="John"
                      error={errors.firstName}
                      editable={!isSubmitting}
                    />
                  </View>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="Last Name"
                      value={lastName}
                      onChangeText={setLastName}
                      placeholder="Doe"
                      editable={!isSubmitting}
                    />
                  </View>
                </View>

                <Input
                  label="Company Name"
                  value={companyName}
                  onChangeText={(text) => {
                    setCompanyName(text);
                    if (errors.companyName) {
                      setErrors(prev => ({ ...prev, companyName: undefined }));
                    }
                  }}
                  placeholder="Acme Corporation"
                  hint={!firstName && !lastName ? 'Required if no name provided' : undefined}
                  error={errors.companyName}
                  editable={!isSubmitting}
                />

                <Input
                  label="Job Title"
                  value={jobTitle}
                  onChangeText={setJobTitle}
                  placeholder="Senior Manager"
                  editable={!isSubmitting}
                />
              </View>

              {/* Contact Information */}
              <View style={modalStyles.section}>
                <Text style={modalStyles.sectionTitle}>Contact Information</Text>
                <Input
                  label="Email"
                  value={email}
                  onChangeText={(text) => {
                    setEmail(text);
                    if (errors.email) {
                      setErrors(prev => ({ ...prev, email: undefined }));
                    }
                  }}
                  placeholder="john.doe@example.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  error={errors.email}
                  editable={!isSubmitting}
                />

                <View style={modalStyles.row}>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="Phone"
                      value={phone}
                      onChangeText={(text) => {
                        setPhone(text);
                        if (errors.phone) {
                          setErrors(prev => ({ ...prev, phone: undefined }));
                        }
                      }}
                      placeholder="(555) 123-4567"
                      keyboardType="phone-pad"
                      error={errors.phone}
                      editable={!isSubmitting}
                    />
                  </View>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="Mobile"
                      value={mobile}
                      onChangeText={(text) => {
                        setMobile(text);
                        if (errors.mobile) {
                          setErrors(prev => ({ ...prev, mobile: undefined }));
                        }
                      }}
                      placeholder="(555) 987-6543"
                      keyboardType="phone-pad"
                      error={errors.mobile}
                      editable={!isSubmitting}
                    />
                  </View>
                </View>

                <Input
                  label="Website"
                  value={website}
                  onChangeText={(text) => {
                    setWebsite(text);
                    if (errors.website) {
                      setErrors(prev => ({ ...prev, website: undefined }));
                    }
                  }}
                  placeholder="example.com"
                  keyboardType="url"
                  autoCapitalize="none"
                  autoCorrect={false}
                  error={errors.website}
                  editable={!isSubmitting}
                />
              </View>

              {/* Address */}
              <View style={modalStyles.section}>
                <Text style={modalStyles.sectionTitle}>Address</Text>
                <Input
                  label="Street Address"
                  value={addressLine1}
                  onChangeText={setAddressLine1}
                  placeholder="123 Main Street"
                  editable={!isSubmitting}
                />

                <Input
                  label="Address Line 2"
                  value={addressLine2}
                  onChangeText={setAddressLine2}
                  placeholder="Suite 100, Apartment 4B"
                  editable={!isSubmitting}
                />

                <View style={modalStyles.row}>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="City"
                      value={city}
                      onChangeText={setCity}
                      placeholder="New York"
                      editable={!isSubmitting}
                    />
                  </View>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="State"
                      value={state}
                      onChangeText={setState}
                      placeholder="NY"
                      editable={!isSubmitting}
                    />
                  </View>
                </View>

                <View style={modalStyles.row}>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="ZIP Code"
                      value={zipCode}
                      onChangeText={setZipCode}
                      placeholder="10001"
                      keyboardType="numeric"
                      editable={!isSubmitting}
                    />
                  </View>
                  <View style={modalStyles.halfWidth}>
                    <Input
                      label="Country"
                      value={country}
                      onChangeText={setCountry}
                      placeholder="USA"
                      editable={!isSubmitting}
                    />
                  </View>
                </View>
              </View>

              {/* Notes */}
              <View style={modalStyles.section}>
                <Text style={modalStyles.sectionTitle}>Notes</Text>
                <Text style={modalStyles.sectionHint}>
                  Add any additional information or reminders about this contact
                </Text>
                <Input
                  value={notes}
                  onChangeText={setNotes}
                  placeholder="Meeting notes, preferences, important dates..."
                  multiline
                  numberOfLines={6}
                  style={modalStyles.textArea}
                  editable={!isSubmitting}
                />
              </View>
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={modalStyles.footer}>
            <Button
              title="Cancel"
              onPress={handleClose}
              variant="outline"
              style={modalStyles.cancelButton}
              disabled={isSubmitting}
            />
            <Button
              title={isSubmitting 
                ? (contact ? 'Updating...' : 'Creating...') 
                : (contact ? 'Update Contact' : 'Create Contact')
              }
              onPress={handleSubmit}
              disabled={isDisabled}
              style={modalStyles.submitButton}
            />
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

function makeStyles(
  styles: ReturnType<typeof useStyles>,
  responsive: ReturnType<typeof useResponsive>,
  isDesktop: boolean,
  tokens: ReturnType<typeof useTheme>['tokens'],
  isMobile: boolean,
  isSmallMobile: boolean
) {
  return StyleSheet.create({
    modalContentCustom: {
      maxWidth: isDesktop ? 700 : '100%',
      width: '100%',
      maxHeight: isMobile ? '100%' : '90%',
      height: isMobile ? '100%' : undefined,
      padding: 0,
      overflow: 'hidden',
      borderRadius: isMobile ? 0 : styles.utils.radius.lg,
      ...(isMobile && {
        borderTopLeftRadius: styles.utils.radius.xl,
        borderTopRightRadius: styles.utils.radius.xl,
      }),
    },
    header: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: styles.utils.spacing.md,
      paddingTop: isMobile ? styles.utils.spacing.lg : styles.utils.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
      flexShrink: 0,
      ...(isMobile && {
        // Add drag indicator for mobile
      }),
    },
    headerContent: {
      flex: 1,
      marginRight: styles.utils.spacing.md,
      minWidth: 0,
    },
    title: {
      fontSize: isMobile ? styles.utils.typography.sizes.lg : styles.utils.typography.sizes.xl,
      fontWeight: styles.utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: styles.utils.typography.heading.fontFamily,
    },
    subtitle: {
      fontSize: styles.utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onBackground, 0.7),
      fontFamily: styles.utils.typography.body.fontFamily,
      marginTop: 2,
    },
    closeButton: {
      width: 36,
      height: 36,
      borderRadius: styles.utils.radius.full,
      backgroundColor: tokens.color.surfaceElevated,
      justifyContent: 'center',
      alignItems: 'center',
      flexShrink: 0,
      ...Platform.select({
        web: { cursor: 'pointer' } as any,
      }),
    },
    closeButtonPressed: {
      opacity: 0.7,
    },
    closeButtonText: {
      fontSize: styles.utils.typography.sizes.lg,
      color: tokens.color.onSurface,
      fontWeight: styles.utils.typography.weights.medium,
    },
    scrollView: {
      flex: 1,
      width: '100%',
    },
    scrollContent: {
      padding: styles.utils.spacing.md,
      paddingBottom: styles.utils.spacing.xl,
    },
    formContainer: {
      width: '100%',
    },
    section: {
      marginBottom: styles.utils.spacing.lg,
      width: '100%',
    },
    sectionTitle: {
      fontSize: styles.utils.typography.sizes.base,
      fontWeight: styles.utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: styles.utils.spacing.xs,
      fontFamily: styles.utils.typography.heading.fontFamily,
    },
    required: {
      color: tokens.color.error,
    },
    sectionHint: {
      fontSize: styles.utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onBackground, 0.7),
      marginBottom: styles.utils.spacing.sm,
      fontFamily: styles.utils.typography.body.fontFamily,
    },
    typeSelector: {
      marginBottom: styles.utils.spacing.sm,
      width: '100%',
    },
    typeSelectorContent: {
      paddingRight: styles.utils.spacing.lg,
      flexDirection: 'row',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: styles.utils.spacing.xs,
    },
    typeChip: {
      paddingHorizontal: styles.utils.spacing.md,
      paddingVertical: styles.utils.spacing.xs,
      borderRadius: styles.utils.radius.md,
      marginRight: 0,
      marginBottom: isMobile ? styles.utils.spacing.xs : 0,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.surface,
    },
    typeChipSelected: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    typeChipPressed: {
      opacity: 0.8,
    },
    typeChipText: {
      fontSize: styles.utils.typography.sizes.sm,
      fontWeight: styles.utils.typography.weights.medium,
      fontFamily: styles.utils.typography.body.fontFamily,
      textAlign: 'center',
      color: tokens.color.onSurface,
    },
    typeChipTextSelected: {
      color: tokens.color.onPrimary,
    },
    selectedTypeHint: {
      fontSize: styles.utils.typography.sizes.xs,
      color: tokens.color.primary,
      fontStyle: 'italic',
      marginTop: styles.utils.spacing.xs,
      fontFamily: styles.utils.typography.body.fontFamily,
    },
    errorText: {
      fontSize: styles.utils.typography.sizes.sm,
      color: tokens.color.error,
      marginTop: styles.utils.spacing.xs,
      fontFamily: styles.utils.typography.body.fontFamily,
      fontWeight: styles.utils.typography.weights.medium,
    },
    row: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: styles.utils.spacing.md,
      width: '100%',
    },
    halfWidth: {
      flex: isMobile ? 0 : 1,
      width: isMobile ? '100%' : undefined,
      minWidth: 0,
    },
    textArea: {
      minHeight: 100,
      textAlignVertical: 'top',
      paddingTop: styles.utils.spacing.md,
    },
    footer: {
      flexDirection: 'row',
      gap: styles.utils.spacing.md,
      padding: styles.utils.spacing.md,
      paddingBottom: isMobile ? styles.utils.spacing.xl : styles.utils.spacing.md,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
      flexShrink: 0,
      backgroundColor: tokens.color.surface,
    },
    cancelButton: {
      flex: 1,
    },
    submitButton: {
      flex: isMobile ? 1 : 2,
    },
  });
}
